import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  Ban,
  Banknote,
  Coins,
  FileText,
  Landmark,
  TriangleAlert,
  Upload,
} from "lucide-react";
import {
  BONUS_TYPE_LABELS,
  COMMISSION_STATUS_LABELS,
  CUSTOMER_TYPE_LABELS,
  PAYOUT_ROUTES,
  PROOF_MAX_BYTES,
  adminUserCommissionsResponseSchema,
  commissionSchema,
  describePayoutMethod,
  formatPrice,
  payoutResponseSchema,
  proofMimeSchema,
  responseSchema,
  type Commission,
  type CommissionStatus,
  type CustomerType,
  type OrderProofInput,
} from "@nh/shared";
import { z } from "zod";
import { authFetch } from "@/shared/api/session";
import { PageHeader } from "@/shared/ui/layout";
import { Alert, Badge, Button, type BadgeVariant } from "@/shared/ui/core";
import { ConfirmDialog, Modal, SkeletonTable } from "@/shared/ui/ui";
import {
  AdminGuard,
  DataTable,
  DataTableFrame,
  SendReminderButton,
  Td,
  Th,
  Tr,
} from "@/features/admin/components";
import { formatOrderDateTime } from "@/features/admin/orders/format";
import { fileToBase64 } from "@/features/kyc/kyc.api";

const NO_PREFIX = { baseUrl: "" } as const;

const STATUS_VARIANTS: Record<CommissionStatus, BadgeVariant> = {
  pending: "warning",
  released: "info",
  paid: "success",
  rejected: "error",
  forfeited: "neutral",
};

const annulResponseSchema = responseSchema(z.object({ commission: commissionSchema }));

type UserCommissions = Extract<
  z.infer<typeof adminUserCommissionsResponseSchema>,
  { ok: true }
>["data"];

function getUserCommissions(userId: string): Promise<UserCommissions> {
  return authFetch(PAYOUT_ROUTES.adminUser(userId), adminUserCommissionsResponseSchema, NO_PREFIX);
}

/**
 * ═══════════════════════════════════════════════════════════════════
 * El expediente de comisiones de UNA persona — la página del sistema
 * viejo, replicada (Anahí, 27-ago): quién es y quién la invitó, su
 * banco (o el aviso de que falta), POR PAGAR y PAGADO, PAGAR TODO con
 * comprobante, cada movimiento con la FRASE de por qué existe, ANULAR
 * lo que no procede, y el historial de pagos con su prueba.
 * ═══════════════════════════════════════════════════════════════════
 */
export function AdminCommissionUserPage() {
  return (
    <AdminGuard>
      <UserCommissionsScreen />
    </AdminGuard>
  );
}

/** "BIR: 20% de la compra de $3,260.00 (PRIMERA compra de Carla), que
 * está en nivel 1 de tu red." — el porqué, en una frase. */
function razonDe(commission: Commission, names: Record<string, string>, t: (k: string, o?: object) => string): string {
  const pct = commission.bpsApplied / 100;
  const base = formatPrice(commission.baseCents, commission.currency);
  const quien = commission.sourceUserId
    ? (names[commission.sourceUserId] ?? t("alguien de la red"))
    : t("el corte del periodo");
  const nivel =
    commission.level !== null
      ? t(", que está en nivel {{n}} de su red", { n: commission.level })
      : "";

  switch (commission.bonusType) {
    case "BIR":
      return t("{{pct}}% de la compra de {{base}} (PRIMERA compra de {{quien}}){{nivel}}.", {
        pct, base, quien, nivel,
      });
    default:
      return t("{{pct}}% sobre {{base}} generados por {{quien}}{{nivel}}.", {
        pct, base, quien, nivel,
      });
  }
}

/** «2026-08» → «agosto de 2026» en el idioma de la pantalla. */
function periodLabel(periodId: string, language: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(periodId);
  if (!match) return periodId;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 15));
  const label = date.toLocaleDateString(language, { month: "long", year: "numeric", timeZone: "UTC" });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function UserCommissionsScreen() {
  const { t, i18n } = useTranslation();
  const { userId = "" } = useParams();
  const queryClient = useQueryClient();

  const detail = useQuery({
    queryKey: ["admin", "commissions", "user", userId],
    queryFn: () => getUserCommissions(userId),
  });

  const [payCurrency, setPayCurrency] = useState<"MXN" | "USD" | null>(null);
  const [annulling, setAnnulling] = useState<Commission | null>(null);
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["admin", "commissions"] });
  };

  /**
   * ── Pagar UNA comisión (Anahí, 29-ago) ─────────────────────────────
   * "Debe poder pagar comisión por comisión, y se debe ir haciendo el
   * descuento de la deuda."
   *
   * Va por el MISMO endpoint que "pagar todo", mandando su id: así el
   * abono queda con su payout, su fecha y su firma, exactamente igual
   * que un pago completo. Al volver, la deuda de arriba baja sola
   * porque se recalcula del servidor, no de un contador local.
   *
   * Sin comprobante: quien paga suelto lo hace desde la fila, y exigir
   * un archivo por cada abono convertiría cinco pagos en cinco
   * formularios. El comprobante sigue estando en "pagar todo", que es
   * donde se cierra la cuenta.
   */
  const pagarUna = useMutation({
    mutationFn: (id: string) =>
      authFetch(PAYOUT_ROUTES.adminPayUser(userId), payoutResponseSchema, {
        ...NO_PREFIX,
        method: "POST",
        body: { commissionIds: [id] },
      }),
    onSuccess: invalidate,
    onError: (cause) =>
      setError(cause instanceof Error ? cause.message : t("No se pudo registrar el pago")),
  });

  const anular = useMutation({
    mutationFn: (id: string) =>
      authFetch(PAYOUT_ROUTES.adminAnnul(id), annulResponseSchema, {
        ...NO_PREFIX,
        method: "PATCH",
        body: {},
      }),
    onSuccess: () => {
      setAnnulling(null);
      invalidate();
    },
    onError: (cause) => {
      setAnnulling(null);
      setError(cause instanceof Error ? cause.message : t("No se pudo anular"));
    },
  });

  const data = detail.data;

  if (detail.isPending || !data) {
    return (
      <>
        <PageHeader titleHidden title={t("Comisiones")} description={t("Abriendo el expediente…")} />
        <SkeletonTable rows={6} columns={5} />
      </>
    );
  }

  const { profile, balances, items, names, payouts } = data;
  const purchaseDates = data.purchaseDates ?? {};
  // La moneda que se va a pagar: `null` = modal cerrado (14-sep: un pago
  // es UNA moneda, y con deuda en dos hay un botón por cada una).
  const payBalance = balances.find((balance) => balance.currency === payCurrency) ?? null;

  // Agrupado por bono, en el orden en que aparecen.
  const groups = new Map<string, Commission[]>();
  for (const item of items) {
    groups.set(item.bonusType, [...(groups.get(item.bonusType) ?? []), item]);
  }

  // Comisión → el PAGO que la liquidó: así cada fila pagada enseña SU
  // comprobante, no uno genérico.
  const payoutByCommission = new Map<string, (typeof payouts)[number]>();
  for (const payout of payouts) {
    for (const commissionId of payout.commissionIds) {
      payoutByCommission.set(commissionId, payout);
    }
  }

  return (
    <>
      <PageHeader
        titleHidden
        title={profile.name}
        description={t("El expediente de comisiones, movimiento por movimiento.")}
      />

      <Link
        to="/admin/commissions"
        className="mb-3 inline-flex items-center gap-1.5 text-[13px] font-semibold text-ds-violet-pop hover:underline"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("Volver a comisiones")}
      </Link>

      {/* ── La cabecera: quién es, su banco y lo que se le debe ───────── */}
      <div className="mb-4 rounded-3xl border border-ds-border bg-white p-6 shadow-ds-float">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-[22px] font-semibold text-ds-ink">{profile.name}</h1>
            <p className="mt-0.5 text-[13px] text-ds-ink-3">
              {profile.customerType
                ? t(CUSTOMER_TYPE_LABELS[profile.customerType as CustomerType])
                : t("Sin posición en la red")}
              {profile.sponsorName &&
                ` · ${t("Invitado por {{name}}", { name: profile.sponsorName })}`}
              {" · "}
              {profile.email}
            </p>
            {profile.hasPayoutMethod && profile.payoutMethod ? (
              <p className="mt-2 inline-flex items-center gap-2 rounded-xl bg-ds-mist/60 px-3 py-1.5 text-[13px] text-ds-ink">
                <Landmark className="h-4 w-4 text-ds-violet-pop" />
                {describePayoutMethod(profile.payoutMethod)}
              </p>
            ) : (
              /*
               * Sin banco: el aviso Y el botón de recordárselo (llega a
               * su campana). Registrada la cuenta, esta rama entera
               * desaparece — recordatorio incluido.
               */
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <p className="inline-flex items-center gap-2 text-[13px] font-medium text-amber-600">
                  <TriangleAlert className="h-4 w-4" />
                  {t("El socio no tiene cuenta bancaria registrada.")}
                </p>
                <SendReminderButton userId={profile.userId} kind="payout_method" />
              </div>
            )}
          </div>

          {/*
            ── Un renglón por MONEDA (14-sep-2026) ─────────────────────
            Pesos y dólares no se suman: cada moneda trae su "por pagar",
            su "pagado" y su propio botón, porque un pago es una sola
            transferencia en una sola moneda.
          */}
          <div className="grid gap-3">
            {balances.map((balance) => (
              <div key={balance.currency} className="flex flex-wrap items-center gap-5">
                <div className="text-right">
                  <p className="text-[11px] font-bold uppercase tracking-ds-label text-ds-ink-3">
                    {t("Por pagar")} · {balance.currency}
                  </p>
                  <p className="text-[22px] font-semibold text-amber-600">
                    {formatPrice(balance.owedCents, balance.currency)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] font-bold uppercase tracking-ds-label text-ds-ink-3">
                    {t("Pagado")} · {balance.currency}
                  </p>
                  <p className="text-[22px] font-semibold text-emerald-600">
                    {formatPrice(balance.paidCents, balance.currency)}
                  </p>
                </div>
                <Button
                  leftIcon={<Banknote />}
                  disabled={balance.owedCents === 0}
                  onClick={() => setPayCurrency(balance.currency)}
                >
                  {balances.length > 1
                    ? t("Pagar todo ({{currency}})", { currency: balance.currency })
                    : t("Pagar todo")}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <Alert variant="error" className="mb-4" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ── Los movimientos, por bono ─────────────────────────────────── */}
      <div className="grid gap-4">
        {[...groups.entries()].map(([bonusType, groupItems]) => {
          const owed = groupItems.filter(
            (item) => item.status === "pending" || item.status === "released",
          );
          // El subtotal del bono, por moneda: "1,000.00 MXN · 100.00 USD".
          const subtotals = new Map<Commission["currency"], number>();
          for (const item of groupItems) {
            subtotals.set(item.currency, (subtotals.get(item.currency) ?? 0) + item.amountCents);
          }
          return (
            <DataTableFrame key={bonusType}>
              <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-ds-border px-5 py-3.5">
                <h2 className="text-[15px] font-semibold text-ds-ink">
                  {BONUS_TYPE_LABELS[bonusType as Commission["bonusType"]]}
                  <span className="ml-2 text-[12px] font-medium text-ds-ink-3">
                    {t("{{n}} por pagar · {{m}} en total", {
                      n: owed.length,
                      m: groupItems.length,
                    })}
                  </span>
                </h2>
                <p className="font-mono text-[14px] font-semibold text-ds-ink">
                  {[...subtotals.entries()]
                    .map(([currency, cents]) => `${formatPrice(cents, currency)} ${currency}`)
                    .join(" · ")}
                </p>
              </div>
              <DataTable caption={t("Comisiones del bono")}>
                <thead>
                  <tr>
                    <Th>{t("Por qué es la comisión")}</Th>
                    <Th align="right">{t("Compra")}</Th>
                    <Th>{t("Moneda")}</Th>
                    <Th>{t("Fecha de compra")}</Th>
                    <Th>{t("Periodo")}</Th>
                    <Th>{t("Estado")}</Th>
                    <Th align="right">{t("Comisión")}</Th>
                    <Th align="right">{t("Acción")}</Th>
                  </tr>
                </thead>
                <tbody>
                  {groupItems.map((commission) => (
                    <Tr key={commission.id}>
                      <Td className="max-w-[440px] text-[13.5px] leading-relaxed">
                        {razonDe(commission, names, t as never)}
                      </Td>
                      <Td align="right" className="font-mono text-[13px]">
                        {formatPrice(commission.baseCents, commission.currency)}
                      </Td>
                      <Td>
                        <Badge variant={commission.currency === "USD" ? "indigo" : "info"} size="sm">
                          {commission.currency}
                        </Badge>
                      </Td>
                      {/*
                        Fecha COMPLETA con hora (Anahí, 14-sep), de la COMPRA que
                        la generó (22-oct): la del registro confundía —el corte
                        de agosto se anotó el 14 de septiembre—.
                      */}
                      <Td className="whitespace-nowrap font-mono text-[12.5px] text-ds-ink-3">
                        {formatOrderDateTime(
                          (commission.sourceOrderId && purchaseDates[commission.sourceOrderId]) ||
                            commission.createdAt,
                        )}
                      </Td>
                      <Td className="font-mono text-[13px] text-ds-ink-3">
                        {/* «Agosto 2026», no «2026-08» (22-oct-2026). */}
                        {periodLabel(commission.periodId, i18n.language)}
                      </Td>
                      <Td>
                        <Badge variant={STATUS_VARIANTS[commission.status]} size="sm" dot>
                          {t(COMMISSION_STATUS_LABELS[commission.status])}
                        </Badge>
                      </Td>
                      <Td align="right" className="font-mono font-semibold text-ds-ink">
                        {formatPrice(commission.amountCents, commission.currency)}
                      </Td>
                      <Td align="right">
                        {commission.status === "pending" || commission.status === "released" ? (
                          <span className="inline-flex items-center gap-1">
                            <Button
                              variant="secondary"
                              size="sm"
                              leftIcon={<Coins />}
                              loading={
                                pagarUna.isPending && pagarUna.variables === commission.id
                              }
                              onClick={() => pagarUna.mutate(commission.id)}
                            >
                              {t("Pagar")}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label={t("Anular")}
                              className="text-red-500 hover:text-red-600"
                              onClick={() => setAnnulling(commission)}
                            >
                              <Ban className="h-4 w-4" />
                            </Button>
                          </span>
                        ) : commission.status === "paid" &&
                          payoutByCommission.get(commission.id)?.proof?.dataUrl ? (
                          <a
                            href={payoutByCommission.get(commission.id)?.proof?.dataUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-ds-violet-pop hover:underline"
                          >
                            <FileText className="h-4 w-4" />
                            {t("Comprobante")}
                          </a>
                        ) : (
                          <span className="text-[12px] text-ds-ink-3">—</span>
                        )}
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </DataTable>
            </DataTableFrame>
          );
        })}

        {/* ── Los pagos hechos, con su prueba ─────────────────────────── */}
        {payouts.length > 0 && (
          <DataTableFrame>
            <div className="border-b border-ds-border px-5 py-3.5">
              <h2 className="text-[15px] font-semibold text-ds-ink">{t("Pagos realizados")}</h2>
            </div>
            <ul className="grid gap-2 p-4">
              {payouts.map((payout) => (
                <li
                  key={payout.id}
                  className="flex flex-wrap items-center gap-3 rounded-2xl border border-ds-border bg-ds-cream-2/40 px-4 py-3"
                >
                  <Banknote className="h-4 w-4 shrink-0 text-emerald-600" />
                  <span className="font-mono text-[14px] font-semibold text-ds-ink">
                    {formatPrice(payout.totalCents, payout.currency)}
                  </span>
                  <span className="text-[13px] text-ds-ink-3">
                    {t("{{n}} comisiones", { n: payout.commissionCount })} ·{" "}
                    {formatOrderDateTime(payout.createdAt)}
                  </span>
                  <span className="ml-auto">
                    {payout.proof?.dataUrl ? (
                      <a
                        href={payout.proof.dataUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-ds-violet-pop hover:underline"
                      >
                        <FileText className="h-4 w-4" />
                        {t("Ver comprobante")}
                      </a>
                    ) : (
                      <span className="text-[12px] text-ds-ink-3">{t("Sin comprobante")}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </DataTableFrame>
        )}
      </div>

      <PayAllModal
        open={payBalance !== null}
        userId={userId}
        userName={profile.name}
        owedCents={payBalance?.owedCents ?? 0}
        currency={payBalance?.currency ?? "MXN"}
        hasBank={profile.hasPayoutMethod}
        onClose={() => setPayCurrency(null)}
        onPaid={() => {
          setPayCurrency(null);
          invalidate();
        }}
      />

      <ConfirmDialog
        open={annulling !== null}
        title={t("Anular la comisión")}
        description={
          annulling
            ? t("Se anulará {{monto}} de {{name}}. Una comisión anulada no se paga.", {
                monto: formatPrice(annulling.amountCents, annulling.currency),
                name: profile.name,
              })
            : ""
        }
        confirmLabel={t("Anular")}
        cancelLabel={t("Cancelar")}
        variant="danger"
        loading={anular.isPending}
        onConfirm={() => {
          if (annulling) anular.mutate(annulling.id);
        }}
        onCancel={() => setAnnulling(null)}
      />
    </>
  );
}

/** PAGAR TODO: el resumen, el comprobante de la transferencia y el acto. */
function PayAllModal({
  open,
  userId,
  userName,
  owedCents,
  currency,
  hasBank,
  onClose,
  onPaid,
}: {
  open: boolean;
  userId: string;
  userName: string;
  owedCents: number;
  currency: "MXN" | "USD";
  hasBank: boolean;
  onClose: () => void;
  onPaid: () => void;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [proof, setProof] = useState<OrderProofInput | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pagar = useMutation({
    mutationFn: () =>
      authFetch(PAYOUT_ROUTES.adminPayUser(userId), payoutResponseSchema, {
        ...NO_PREFIX,
        method: "POST",
        // La moneda viaja SIEMPRE: un pago es una sola moneda (14-sep).
        body: { currency, ...(proof ? { proof } : {}) },
      }),
    onSuccess: () => {
      setProof(null);
      onPaid();
    },
    onError: (cause) =>
      setError(cause instanceof Error ? cause.message : t("No se pudo registrar el pago")),
  });

  async function onFile(file: File | undefined) {
    setError(null);
    if (!file) return;
    const mime = proofMimeSchema.safeParse(file.type);
    if (!mime.success) {
      setError(t("Solo se aceptan fotos (JPG, PNG, WebP) o PDF."));
      return;
    }
    if (file.size > PROOF_MAX_BYTES) {
      setError(t("El comprobante no puede pasar de 4 MB."));
      return;
    }
    setProof({ fileName: file.name, mimeType: mime.data, dataBase64: await fileToBase64(file) });
  }

  return (
    <Modal open={open} onClose={onClose} title={t("Pagar todo a {{name}}", { name: userName })}>
      <div className="grid gap-4">
        <p className="text-[14px] leading-relaxed text-ds-ink-3">
          {t("Se marcarán como PAGADAS todas las comisiones debidas en {{currency}}, por un total de", { currency })}{" "}
          <span className="font-mono font-semibold text-ds-ink">
            {formatPrice(owedCents, currency)}
          </span>
          .
        </p>
        {!hasBank && (
          <Alert variant="warning">
            {t("Esta cuenta no tiene banco registrado: confirma con la persona a dónde transferir antes de pagar.")}
          </Alert>
        )}

        <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-ds-border bg-ds-cream-2/40 px-4 py-3">
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            className="hidden"
            onChange={(event) => {
              void onFile(event.target.files?.[0]);
              event.target.value = "";
            }}
          />
          <FileText className="h-4 w-4 shrink-0 text-ds-ink-3" />
          <span className="min-w-0 flex-1 truncate text-[13px] text-ds-ink-3">
            {proof ? proof.fileName : t("Comprobante de la transferencia (recomendado)")}
          </span>
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Upload />}
            onClick={() => inputRef.current?.click()}
          >
            {proof ? t("Cambiar") : t("Subir")}
          </Button>
        </div>

        {error && (
          <Alert variant="error" onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose}>
            {t("Cancelar")}
          </Button>
          <Button leftIcon={<Banknote />} loading={pagar.isPending} onClick={() => pagar.mutate()}>
            {t("Registrar el pago")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
